import axios from 'axios';
import { createSlice } from '@reduxjs/toolkit';
import url from '../../constants/config';
import { toasterOn } from '../../shared/store/actions';

const getAuthHeader = () => ({
  Authorization: `Bearer ${window.localStorage.getItem('authorization')}`,
});

export const adminOversightSlice = createSlice({
  name: 'adminOversight',
  initialState: {
    conversations: [],
    activeThread: null,
    messages: [],
    nextCursor: null,
    unreadCounts: {
      coach: 0,
      assistant: 0,
      total: 0,
    },
    loading: false,
  },
  reducers: {
    setConversations: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.conversations = action.payload;
    },
    setActiveThread: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.activeThread = action.payload.conversation;
      // eslint-disable-next-line no-param-reassign
      state.messages = action.payload.messages;
      // eslint-disable-next-line no-param-reassign
      state.nextCursor = action.payload.nextCursor;
    },
    prependMessages: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.messages = [...action.payload.messages, ...state.messages];
      // eslint-disable-next-line no-param-reassign
      state.nextCursor = action.payload.nextCursor;
    },
    appendMessage: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.messages = [...state.messages, action.payload];
    },
    setLoading: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.loading = action.payload;
    },
    setUnreadCounts: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.unreadCounts = action.payload;
    },
    clearOversightThread: state => {
      // eslint-disable-next-line no-param-reassign
      state.activeThread = null;
      // eslint-disable-next-line no-param-reassign
      state.messages = [];
      // eslint-disable-next-line no-param-reassign
      state.nextCursor = null;
    },
  },
});

export const {
  setConversations,
  setActiveThread,
  prependMessages,
  appendMessage,
  setLoading,
  setUnreadCounts,
  clearOversightThread,
} = adminOversightSlice.actions;

export const fetchOversightConversations = participantRole => async dispatch => {
  try {
    dispatch(setLoading(true));
    const res = await axios.get(
      `${url}/admin-oversight?participantRole=${participantRole}`,
      { headers: getAuthHeader() }
    );
    dispatch(setConversations(res.data));
  } catch (err) {
    dispatch(toasterOn({ message: 'Failed to load conversations', type: 'failed' }));
  } finally {
    dispatch(setLoading(false));
  }
};

export const fetchOversightUnreadCounts = () => async dispatch => {
  try {
    const res = await axios.get(`${url}/admin-oversight/unread-counts`, {
      headers: getAuthHeader(),
    });
    dispatch(setUnreadCounts(res.data));
  } catch (err) {
    // silently fail — non-critical background poll
  }
};

export const fetchOversightThread = id => async dispatch => {
  try {
    dispatch(setLoading(true));
    const res = await axios.get(`${url}/admin-oversight/${id}`, { headers: getAuthHeader() });
    dispatch(setActiveThread(res.data));
  } catch (err) {
    dispatch(toasterOn({ message: 'Failed to load conversation', type: 'failed' }));
  } finally {
    dispatch(setLoading(false));
  }
};

export const fetchOversightMoreMessages = (id, cursor) => async dispatch => {
  try {
    const res = await axios.get(`${url}/admin-oversight/${id}/messages?before=${cursor}`, {
      headers: getAuthHeader(),
    });
    dispatch(prependMessages(res.data));
  } catch (err) {
    dispatch(toasterOn({ message: 'Failed to load older messages', type: 'failed' }));
  }
};

export const markOversightConversationUnread = conversationId => async dispatch => {
  try {
    await axios.patch(
      `${url}/admin-oversight/${conversationId}/mark-unread`,
      {},
      { headers: getAuthHeader() }
    );
  } catch (err) {
    // silently fail
  }
};

export const sendOversightMessage = ({ conversationId, text, file, draftRevision }) => async dispatch => {
  try {
    let response;
    const headers = getAuthHeader();
    if (file) {
      const formData = new FormData();
      if (draftRevision !== undefined) formData.append('draftRevision', String(draftRevision));
      if (text) formData.append('text', text);
      formData.append('attachment', file);
      response = await axios.post(
        `${url}/admin-oversight/${conversationId}/messages`,
        formData,
        { headers }
      );
    } else {
      response = await axios.post(
        `${url}/admin-oversight/${conversationId}/messages`,
        { text, draftRevision },
        { headers }
      );
    }
    dispatch(appendMessage(response.data.message));
    return response.data.message;
  } catch (err) {
    dispatch(toasterOn({ message: 'Failed to send message', type: 'failed' }));
    return null;
  }
};

export default adminOversightSlice.reducer;
